import type { CardAccessClaimResult } from '@lub0v/santa-utils';

/** Session payload — create/rotate responses plus optional client flags. */
export type StoredOrganizerAccess = CardAccessClaimResult & {
  /** Guest account “create new codes” — QD participants only see codes with this set. */
  rotatedFromSettings?: boolean;
};

/**
 * A guest organizer gets recovery codes once, in the create response — the box
 * page is what shows them. Since the draw hands off to that page with a full
 * page load, the codes travel through sessionStorage.
 *
 * The key is santa-frontend's (`qd_organizer_access:<boxKey>`), so a draw
 * started in either version of the app can be picked up by the other.
 */
const KEY_PREFIX = 'qd_organizer_access:';

function storageKey(boxKey: string): string {
  return `${KEY_PREFIX}${boxKey}`;
}

function isStoredOrganizerAccess(value: unknown): value is StoredOrganizerAccess {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const result = value as StoredOrganizerAccess;
  return (
    typeof result.redirect === 'string' &&
    typeof result.cardUrl === 'string' &&
    Array.isArray(result.recoveryCodes) &&
    result.recoveryCodes.length > 0
  );
}

export function storeOrganizerAccess(
  boxKey: string,
  data: CardAccessClaimResult,
  options?: { rotatedFromSettings?: boolean },
): void {
  if (typeof window === 'undefined' || !boxKey || !isStoredOrganizerAccess(data)) {
    return;
  }
  const payload: StoredOrganizerAccess = {
    ...data,
    ...(options?.rotatedFromSettings ? { rotatedFromSettings: true } : {}),
  };
  sessionStorage.setItem(storageKey(boxKey), JSON.stringify(payload));
}

/** Reads without consuming: the codes stay until the organizer moves past them. */
export function peekOrganizerAccess(boxKey: string): StoredOrganizerAccess | undefined {
  if (typeof window === 'undefined' || !boxKey) {
    return undefined;
  }
  const raw = sessionStorage.getItem(storageKey(boxKey));
  if (!raw) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isStoredOrganizerAccess(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function clearOrganizerAccess(boxKey: string): void {
  if (typeof window === 'undefined' || !boxKey) {
    return;
  }
  sessionStorage.removeItem(storageKey(boxKey));
}
